<template>
  <div id='un_product_art_group'
    class='z_4'
    :class='[lastDragEvent, {droppable: droppable}]'
    @drag="handleDragDrop('drag', $event)"
    @dragstart="handleDragDrop('dragstart', $event)"
    @dragend="handleDragDrop('dragend', $event)"
    @dragover="handleDragDrop('dragover', $event)"
    @dragenter="handleDragDrop('dragenter', $event)"
    @dragleave="handleDragDrop('dragleave', $event)"
    @drop="handleDragDrop('drop', $event)">

    <div v-if='droppable' class='droppable-overlay z_3'>

      <p>
        <IconFileUpload />
        <br/>
        Drop your image here.
      </p>
    </div>

    <div v-if='!userImageExists' class='add-image-btn'>
      <button class='btn btn-secondary' v-tap='promptUserForImageFile'>Add Image +</button>
    </div>

    <canvas id='un_product_art_board' class='z_2 over_h'>
    </canvas>
  </div>
</template>

<script>
import {isAdvancedUpload} from '$utilities/window'
import IconFileUpload from '$svgs/IconFileUpload.vue'
import {
  HIDE_INTRO_EVENT,
  PROMPT_USER_FOR_IMAGE_FILE_EVENT,
  RENDER_COMPLETE_EVENT,
  unUi
} from '@/components/EventBuses'

export default {
  props: [
    'masks',
    'canvas',
    'userImageExists',
    'currentImage'
  ],

  components: {
    IconFileUpload
  },

  data () {
    return {
      lastDragEvent: null,
      mousedown: false,
      mousedownpos: {
        x: null,
        y: null
      }
    }
  },

  watch: {
    canvas: function (value) {
      if (value) {
        this.bindMaskEvents()
      }
    }
  },

  computed: {
    droppable: function () {
      switch (this.lastDragEvent) {
        case 'drop':
        case 'dragleave':
          return false
        case 'dragover':
          return true
        default:
          return false
      }
    }
  },

  methods: {
    /**
     * Handles and emits artboard drag events
     * @param {String} eventType - type of drag event
     * @param {Object} $event - returned event obj from original event
     */
    handleDragDrop (eventType, event) {
      if (!isAdvancedUpload) {
        this.$emit('dragNotSupported')
        return
      }

      this.lastDragEvent = eventType

      let targetedMask

      event.preventDefault()
      event.stopPropagation()

      if (event.srcElement.className.indexOf('upper-canvas') !== -1) {
        let posData = {
          x: event.offsetX,
          y: event.offsetY
        }
        targetedMask = this.targetMask(posData)
      }

      if (eventType === 'dragover' || eventType === 'dragenter') {
        this.$emit('dragOverEvent', true)
      }

      if (eventType === 'dragleave' || eventType === 'dragend' || eventType === 'drop') {
        this.$emit('dragOverEvent', false)
      }

      if (eventType === 'drop') {
        // TODO, Hack.
        if (!targetedMask) {
          targetedMask = this.masks[0]
        }

        this.$emit('draggedIn', targetedMask)
        this.$emit('fileChange', 'media_file', event.dataTransfer.files)
      }
    },

    /**
     * Event handler and emitter for intro screen and
     * 'add image' button when artboard is empty
     */
    promptUserForImageFile () {
      this.$emit('setSelectedMask', this.masks[0])
      this.$emit('viaDialog')
      this.$emit('selectImg', this.masks[0])
      this.$emit('fileSelectionTriggered')
      unUi.$emit(HIDE_INTRO_EVENT)
    },

    /**
     * Return the currently active mask using mouse coords
     * @param {Object} mouseData mouse pointer data
     * @returns the currently active mask
     */
    targetMask (mouseData) {
      let activeMask

      this.masks.forEach(mask => {
        /* for future: a lot of this logic can get moved to
         * setmaskevents() in designproduct.vue. it has more
         * robust hit detection built in from fabric.
         */
        let left = mask.x
        let top = mask.y
        let right = left + mask.width
        let bottom = top + mask.height

        if ((mouseData.x > left) && (mouseData.x < right) && (mouseData.y > top) && (mouseData.y < bottom)) {
          activeMask = mask
        }
      })

      return activeMask
    },

    /**
     * Handle mouse down event - set the mouse down coords into state
     * and emit the selected mask and image if it exists.
     * @param {Object} mask - the targetted mask
     * @param {Object} e - event obj from original mouse event
     */
    handleMouseDown (event) {
      let mask = this.targetMask(event.pointer)

      this.mousedown = true
      this.mousedownpos.x = event.pointer.x
      this.mousedownpos.y = event.pointer.y

      this.$emit('setSelectedMask', mask)

      if (mask.img) {
        this.$emit('selectImg', mask)
      }
    },

    /**
     * Handle mouse up event - check if the mouse up coords are the same as mouse down coords
     * and if so and there isn't already an image in the mask, emit the file dialog events
     * @param {Object} mask - the targetted mask
     * @param {Object} e - event obj from original mouse event
     */
    handleMouseUp (event) {
      let mask = this.targetMask(event.pointer)

      this.mousedown = false

      if ((this.mousedownpos.x === event.pointer.x) && (this.mousedownpos.y === event.pointer.y) && !mask.img) {
        this.$emit('viaDialog')
        this.$emit('fileSelectionTriggered')
      }

      if (mask && mask.img) {
        this.$emit('selectImg', mask)
      }
    },

    /**
     * Bind canvas events
     */
    bindMaskEvents () {
      const vm = this

      if (this.canvas) {
        this.canvas.on('mouse:down', this.handleMouseDown)
        this.canvas.on('mouse:up', this.handleMouseUp)

        this.canvas.on('object:moving', function (e) {
          vm.imageBoundaryCheck(e.target, vm.currentImage.exifNumber, vm.canvas)
        })

        this.canvas.on('object:moved', (e) => {
          unUi.$emit('scaleChanged')
        })

        this.canvas.on('after:render', function (e) {
          unUi.$emit(RENDER_COMPLETE_EVENT)
        })
      }
    },

    /**
     * Determine what side(s) of the image are revealing the artboard
     * And lock their position to their respective artboard's side
     * @param {Object} image a fabric image object
     * @param {Number} exifNumber exif orientation number
     * @param {Object} canvas the given fabric canvas instance
     */
    imageBoundaryCheck (image, exifNumber, canvas, isAnimated) {
      let imageObjLeft = image.left
      let imageObjTop = image.top
      let imgObjWidth = image.width
      let imgObjHeight = image.height

      /*
       * If image's orientation is compensating for found exif rotation data
       * and the orientation is 90 or 270 degrees, swap the width/height so that
       * those dimensions coorespond with the exif-driven orientation.
       * i.e. The fabricjs- height property of the image is actually visually
       * corresponding with the width because we rotated the image based on an exif #
       */
      if (exifNumber && (exifNumber === 6 || exifNumber === 8)) {
        if ((image.angle / 90) % 2 === 0) {
          imgObjWidth = image.width
          imgObjHeight = image.height
        } else {
          imgObjWidth = image.exifCompensatedWidth
          imgObjHeight = image.exifCompensatedHeight
        }
      } else {
        if ((image.angle / 90) % 2 !== 0) {
          imgObjWidth = image.height
          imgObjHeight = image.width
        }
      }

      let imageCenterX = (imgObjWidth * image.scaleX) / 2
      let imageCenterY = (imgObjHeight * image.scaleY) / 2
      let imgOffsetX = canvas.width - imageObjLeft
      let imgOffsetY = canvas.height - imageObjTop

      if (imageObjLeft >= (imageCenterX - 1)) {
        image.set({left: imageCenterX})
      }
      if (imgOffsetX >= (imageCenterX - 1)) {
        image.set({left: canvas.width - imageCenterX})
      }
      if (imageObjTop >= imageCenterY) {
        image.set({top: imageCenterY})
      }
      if (imgOffsetY >= imageCenterY) {
        image.set({top: canvas.height - imageCenterY})
      }
    }
  },
  mounted () {
    this.bindMaskEvents()

    unUi.$on(PROMPT_USER_FOR_IMAGE_FILE_EVENT, this.promptUserForImageFile)
  }
}
</script>

<style lang='scss'>
#un_product_art_group {
  @include full_wh;

  // position: relative;
  width: 100%;
  height: calc(100% - 28rem);
  left: 50%;
  top: 7.2rem;
  transform: translateX(-50%);

  .droppable-overlay {
    pointer-events: none;
    position: absolute;
    top: 2rem;
    left: 2rem;
    right: 2rem;
    bottom: 2rem;
    border: 8px dashed rgba(#000, 0.5);
    border-radius: 2rem;
    background: rgba(#000, 0.6);

    p {
      color: #FFF;
      text-align: center;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translateX(-50%) translateY(-50%);
      font-size: 2rem;
      text-shadow: 0px 0px 10px rgba(#000, 0.5);

      .icon-file-upload {
        filter: drop-shadow(0px 0px 10px rgba(#000, 0.5));
        width: 100px;
        margin: 0 auto 3rem auto;
        display: inline-block;
      }
    }

  }

  .canvas-container {
    position: relative;
    top: 50%;
    margin: 0 auto;
    transform: translateY(-50%);
  }

  #un_product_art_board {
    @include full_wh;
  }

  @include respond-to("small") {
    // top: 5rem;
  }

  &.droppable {
    //
  }
}

.add-image-btn {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;

  button.btn.btn-secondary {
    position: relative;
    margin: 0 auto;
    display: block;
    top: 50%;
    transform: translateY(-50%);
    background-color: white !important;
    box-shadow: 0px 0px 25px 0px rgba(0, 0, 255, 0.18);
    border: 2px solid #FFFFFF !important;
    padding: 0.8rem 2rem;
    font-size: 1.2rem;

    &:focus {
      outline:none;
    }
  }

  p {
    text-align: center;
    position: relative;
    top: 50%;
    transform: translateY(-50%);
    width: 40%;
    margin: 0 auto;
    @include font-size(1.1);
    color: blue;
  }
}
</style>



// WEBPACK FOOTER //
// uncommon-client/components/product-designer/ArtCanvas.vue